import React, { useState, useEffect, useMemo } from "react";
import { Loader2, X, Package, ShieldCheck, FileText, Sparkles, Database, Trophy, Scale, BookOpen, AlertTriangle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOSSIER_PROMPTS_INFO = [
  { id: "preset_go_nogo", name: "GO / NO-GO Express", Icon: Trophy, color: "amber" },
  { id: "preset_riesgos_legales", name: "Riesgos legales y contractuales", Icon: Scale, color: "rose" },
  { id: "preset_comite", name: "Resumen para Comité", Icon: BookOpen, color: "sky" },
  { id: "preset_1pager", name: "Resumen 1 página", Icon: FileText, color: "emerald" },
];

/**
 * Dossier 1-click: ejecuta compliance + burst de 4 síntesis + RAG histórico
 * en paralelo. Resultado: hasta 5 documentos listos en ~90s.
 */
export default function LicitacionDossierModal({ open, onClose, lic, onGenerated }) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [propuestaFileId, setPropuestaFileId] = useState(null);
  const [pliegoFileId, setPliegoFileId] = useState(null);
  const [synthesisModel, setSynthesisModel] = useState("claude-sonnet-4-6");
  const [includeCompliance, setIncludeCompliance] = useState(true);
  const [includeRag, setIncludeRag] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState(0);

  // Cargar sesiones completed al abrir
  useEffect(() => {
    if (!open || !lic?.id) return;
    axios.get(`${API}/licitaciones/${lic.id}/ai-analysis/sessions`)
      .then(r => {
        const comp = (r.data?.sessions || []).filter(s => s.status === "completed");
        setSessions(comp);
        if (comp.length === 1) setSessionId(comp[0].id);
      })
      .catch(() => setSessions([]));
    setResult(null);
    setElapsed(0);
  }, [open, lic?.id]);

  // Autodetectar pliego/propuesta
  const textFiles = useMemo(() => (lic?.archivos || []).filter(f => (f.texto_extraido || "").length > 200), [lic?.archivos]);
  useEffect(() => {
    if (!open) return;
    let pl = null, pr = null;
    for (const f of textFiles) {
      const n = (f.nombre || "").toLowerCase();
      if (/(ppt|pcap|pliego|prescripciones|administrativ)/.test(n) && !pl) pl = f.id;
      if (/(propuesta|oferta|memoria|t[eé]cnica)/.test(n) && !pr) pr = f.id;
    }
    setPliegoFileId(pl);
    setPropuestaFileId(pr);
  }, [open, textFiles]);

  // Timer
  useEffect(() => {
    if (!running) return;
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(id);
  }, [running]);

  if (!open) return null;

  const selSession = sessions.find(s => s.id === sessionId);
  const burstUnitCost = selSession ? (
    synthesisModel === "claude-opus-4-8" ? selSession.resume_cost_opus_usd : selSession.resume_cost_sonnet_usd
  ) : 0;
  const estimatedTotal = (burstUnitCost * 4) + (includeCompliance && propuestaFileId ? 0.15 : 0);

  const launch = async () => {
    if (!sessionId) return toast.error("Selecciona una sesión Pando completed");
    if (includeCompliance && !propuestaFileId) {
      return toast.error("Para compliance check necesitas seleccionar la propuesta");
    }
    setRunning(true);
    setResult(null);
    try {
      // Iter 118: POST async → 202 con dossier_id, luego polling
      const r = await axios.post(`${API}/licitaciones/${lic.id}/ai-analysis/dossier`, {
        session_id: sessionId,
        propuesta_file_id: includeCompliance ? propuestaFileId : null,
        pliego_file_id: includeCompliance ? pliegoFileId : null,
        synthesis_model: synthesisModel,
        include_compliance: includeCompliance,
        include_rag: includeRag,
      });
      const dossierId = r.data?.dossier_id;
      if (!dossierId) throw new Error("Respuesta 202 sin dossier_id");
      toast.info("📦 Dossier en marcha · procesando en background…", { duration: 4000 });

      // Poll cada 3s, timeout máx 8 min
      const started = Date.now();
      let job = null;
      while (Date.now() - started < 8 * 60 * 1000) {
        await new Promise(res => setTimeout(res, 3000));
        try {
          const s = await axios.get(`${API}/licitaciones/ai-analysis/dossier-status/${dossierId}`);
          job = s.data;
          setProgress(job?.progress || 0);
          if (job?.status === "done" || job?.status === "failed") break;
        } catch (pe) {
          // Retry silently on transient errors
          console.warn("dossier poll transient error", pe?.message);
        }
      }
      if (!job || job.status !== "done") {
        throw new Error(job?.error || `Dossier no completado (status=${job?.status || "timeout"})`);
      }
      setResult(job);
      const files = job.total_files_generated || 0;
      toast.success(`📦 Dossier listo · ${files} documentos · $${job.total_cost_usd} · ${job.elapsed_s}s`);
      onGenerated && onGenerated();
    } catch (e) {
      toast.error("Error dossier: " + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
      setProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="dossier-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2.5">
            <Package size={22} />
            <div>
              <h3 className="font-bold text-base">Dossier 1-click</h3>
              <p className="text-[11px] opacity-90">Compliance + 4 síntesis + RAG histórico · paralelo · ~60-120s</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="dossier-close" disabled={running}>
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!result ? (
            sessions.length === 0 ? (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 text-[12px] text-amber-900">
                <AlertTriangle className="inline mr-1.5" size={14} />
                Necesitas al menos <b>una sesión Pando completada</b> (modo Por lotes) para lanzar un dossier. Ve a &quot;Generar análisis Pando&quot; → modo &quot;Por lotes&quot; → completa el análisis. Después vuelve aquí.
              </div>
            ) : (
              <>
                {/* Selector de sesión */}
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wide text-gray-700 block mb-1.5">
                    🎯 Sesión Pando base (extractos a reutilizar)
                  </label>
                  <select
                    value={sessionId || ""}
                    onChange={e => setSessionId(e.target.value || null)}
                    data-testid="dossier-session-select"
                    className="w-full text-[12px] border border-gray-300 rounded px-2 py-1.5">
                    <option value="">— elige sesión —</option>
                    {sessions.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.prompt_key} · {s.batches_total} lotes · {new Date(s.finished_at || s.started_at).toLocaleDateString("es-ES")}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Toggles */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className={`border rounded-lg p-3 cursor-pointer transition ${includeCompliance ? "border-emerald-400 bg-emerald-50" : "border-gray-200 hover:bg-gray-50"}`} data-testid="dossier-toggle-compliance">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-800 flex items-center gap-1.5">
                        <ShieldCheck size={14} className="text-emerald-600" /> Compliance Check
                      </span>
                      <input type="checkbox" checked={includeCompliance} onChange={e => setIncludeCompliance(e.target.checked)} className="accent-emerald-600"/>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5">Compara pliego ↔ propuesta · matriz de requisitos</p>
                  </label>
                  <label className={`border rounded-lg p-3 cursor-pointer transition ${includeRag ? "border-sky-400 bg-sky-50" : "border-gray-200 hover:bg-gray-50"}`} data-testid="dossier-toggle-rag">
                    <div className="flex items-center justify-between">
                      <span className="text-[12px] font-bold text-gray-800 flex items-center gap-1.5">
                        <Database size={14} className="text-sky-600" /> Inyectar RAG histórico
                      </span>
                      <input type="checkbox" checked={includeRag} onChange={e => setIncludeRag(e.target.checked)} className="accent-sky-600"/>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-0.5">Histórico del municipio {lic?.municipio ? `(${lic.municipio})` : ""} en cada síntesis</p>
                  </label>
                </div>

                {/* Selectores compliance */}
                {includeCompliance && (
                  <div className="bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 space-y-2" data-testid="dossier-compliance-files">
                    {textFiles.length < 2 ? (
                      <div className="text-[11px] text-amber-800">
                        <AlertTriangle className="inline mr-1" size={12} />
                        No hay suficientes archivos con texto extraído para compliance. Sube pliego+propuesta o desmarca esta opción.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-bold text-emerald-900 block mb-1">📋 Pliego</label>
                          <select
                            value={pliegoFileId || ""}
                            onChange={e => setPliegoFileId(e.target.value || null)}
                            data-testid="dossier-pliego-select"
                            className="w-full text-[11px] border border-emerald-300 rounded px-2 py-1 bg-white">
                            <option value="">— auto —</option>
                            {textFiles.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-emerald-900 block mb-1">📝 Propuesta</label>
                          <select
                            value={propuestaFileId || ""}
                            onChange={e => setPropuestaFileId(e.target.value || null)}
                            data-testid="dossier-propuesta-select"
                            className="w-full text-[11px] border border-emerald-300 rounded px-2 py-1 bg-white">
                            <option value="">— elige propuesta —</option>
                            {textFiles.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Modelo síntesis */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase font-bold text-gray-700 tracking-wide">Modelo síntesis</span>
                  <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 bg-white" data-testid="dossier-model-selector">
                    {[
                      { v: "claude-sonnet-4-6", label: "Sonnet 4.6", sub: "$3/$15" },
                      { v: "claude-opus-4-8",   label: "Opus 4.8",   sub: "$5/$25" },
                    ].map(opt => (
                      <button
                        key={opt.v}
                        onClick={() => setSynthesisModel(opt.v)}
                        data-testid={`dossier-model-${opt.v}`}
                        className={`text-[11px] px-2.5 py-1 font-semibold transition-colors ${
                          synthesisModel === opt.v ? "bg-purple-600 text-white" : "text-gray-700 hover:bg-purple-50"
                        }`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview de lo que va a generar */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-600">📦 Contenido del dossier ({(includeCompliance && propuestaFileId ? 1 : 0) + 4} documentos)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {includeCompliance && propuestaFileId && (
                      <div className="flex items-center gap-2 bg-white rounded p-2 border border-emerald-200 text-[11px]">
                        <ShieldCheck size={14} className="text-emerald-600"/>
                        <span className="font-semibold">Compliance pliego↔propuesta</span>
                      </div>
                    )}
                    {DOSSIER_PROMPTS_INFO.map(p => {
                      const Ic = p.Icon;
                      return (
                        <div key={p.id} className={`flex items-center gap-2 bg-white rounded p-2 border border-${p.color}-200 text-[11px]`}>
                          <Ic size={14} className={`text-${p.color}-600`}/>
                          <span className="font-semibold">{p.name}</span>
                        </div>
                      );
                    })}
                  </div>
                  {includeRag && (
                    <div className="text-[10px] text-sky-800 bg-sky-50 rounded p-1.5 border border-sky-200">
                      <Database size={11} className="inline mr-1"/>
                      Cada síntesis incluirá contexto histórico de {lic?.municipio || "el municipio"} (win rate, ganador típico, huecos).
                    </div>
                  )}
                  <div className="text-[11px] text-purple-800 bg-purple-50 rounded p-2 border border-purple-200 font-semibold">
                    💰 Coste estimado total: <b>~${estimatedTotal.toFixed(3)}</b> · Tiempo estimado: 60-120s
                  </div>
                </div>

                <button
                  onClick={launch}
                  disabled={running || !sessionId}
                  data-testid="dossier-launch"
                  className="w-full bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600 hover:opacity-90 text-white font-bold text-sm py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {running ? (
                    <><Loader2 className="animate-spin" size={16}/> Ejecutando dossier ({elapsed}s · {progress}%)…</>
                  ) : (
                    <><Sparkles size={16}/> 🚀 Lanzar Dossier 1-click</>
                  )}
                </button>
                {running && (
                  <div className="mt-2 h-1.5 w-full bg-purple-100 rounded overflow-hidden" data-testid="dossier-progress-bar">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all duration-500" style={{ width: `${progress}%` }}/>
                  </div>
                )}
              </>
            )
          ) : (
            <div data-testid="dossier-result" className="space-y-3">
              <div className="bg-gradient-to-r from-emerald-50 to-sky-50 border-2 border-emerald-300 rounded-lg p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <CheckCircle2 className="text-emerald-600" size={28}/>
                  <div className="flex-1">
                    <div className="text-[13px] font-bold text-emerald-900">📦 Dossier completado</div>
                    <div className="text-[11px] text-gray-700">
                      {result.total_files_generated} documentos generados · ${result.total_cost_usd} · {result.elapsed_s}s
                    </div>
                  </div>
                </div>
              </div>

              {/* Resultados burst */}
              {result.burst?.results && (
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase font-bold tracking-wide text-purple-800">Síntesis ({result.burst.succeeded_count} OK / {result.burst.failed_count} KO)</div>
                  {result.burst.results.map(r => (
                    <div key={r.prompt_id} className={`flex items-center gap-2 rounded p-2 text-[11px] ${r.file_id ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}>
                      <span>{r.file_id ? "✅" : "❌"}</span>
                      <span className="flex-1 font-semibold">{r.prompt_name}</span>
                      {r.file_id ? (
                        <span className="text-gray-600">{(r.length||0).toLocaleString("es-ES")} chars · ${r.cost_usd}</span>
                      ) : (
                        <span className="text-rose-700 truncate" title={r.error}>{(r.error || "").slice(0, 60)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Compliance */}
              {result.compliance && !result.compliance.skipped && !result.compliance.error && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="text-emerald-600" size={14}/>
                    <span className="text-[11px] font-bold">Compliance · Score {result.compliance.score_global}/100 · {result.compliance.veredicto}</span>
                  </div>
                  <p className="text-[11px] text-gray-700">{result.compliance.resumen_ejecutivo}</p>
                </div>
              )}
              {result.compliance?.error && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800">
                  Compliance no ejecutado: {result.compliance.error}
                </div>
              )}

              {/* RAG */}
              {result.rag_municipio && (
                <div className="bg-sky-50 border border-sky-200 rounded p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Database className="text-sky-600" size={14}/>
                    <span className="text-[11px] font-bold">Contexto histórico inyectado · {result.rag_municipio.municipio}</span>
                  </div>
                  <p className="text-[11px] text-gray-700">
                    {result.rag_municipio.total_licitaciones} licitaciones previas · Win rate {result.rag_municipio.win_rate} · Ganador típico: {result.rag_municipio.ganador_tipico}
                  </p>
                </div>
              )}

              <div className="text-[11px] text-gray-500 bg-gray-50 rounded p-2 border border-gray-200">
                Los archivos generados están en la pestaña <b>Documentos</b> de la licitación.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
